'use client';

import React from 'react';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { useSearchParams } from 'next/navigation';
import { Eyebrow, SectionIntro } from '@web/atoms';
import { CtaButton } from '@web/molecules';
import { CTAGroup } from '../CTAGroup';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import {
  StyledSimplifiedHero,
  ButtonWrapper,
  EyebrowWrapper,
} from './styles.js';

export const SimplifiedHero = ({
  heading,
  headingVariants,
  description,
  backgroundColor = 'background.base',
  link,
  level,
  eyebrow,
  typography,
  fontColor = 'text.primary',
  mobilePadding,
  desktopPadding,
  textAlignment = 'center',
}) => {
  const searchParams = useSearchParams();
  const headingVariant = searchParams?.get('hpkeyword');

  if (headingVariant && headingVariants) {
    const variant = headingVariants.find(e => e.variant === headingVariant);
    if (variant) {
      heading = variant.heading;
    }
  }

  const { formattedAmount } = useShopifyOfferContext();

  const formattedHeading = interpolate(heading, {
    offer: formattedAmount,
  });
  const ctaAlignment = link?.alignment || textAlignment;

  return (
    <StyledSimplifiedHero
      $backgroundColor={backgroundColor}
      $mobilePadding={mobilePadding}
      $desktopPadding={desktopPadding}
    >
      {eyebrow && (
        <EyebrowWrapper $textAlignment={textAlignment}>
          <Eyebrow textAlign={textAlignment}>{eyebrow}</Eyebrow>
        </EyebrowWrapper>
      )}
      <SectionIntro
        fontColor={fontColor}
        headline={formattedHeading}
        subheadline={description}
        headingLevel={level}
        typography={typography}
        textAlignment={textAlignment}
      />
      {link &&
        (link.component === 'cta_group' ? (
          <ButtonWrapper
            $backgroundColor={backgroundColor}
            $textAlignment={ctaAlignment}
          >
            <CTAGroup {...link} />
          </ButtonWrapper>
        ) : (
          <ButtonWrapper
            $backgroundColor={backgroundColor}
            $textAlignment={ctaAlignment}
          >
            <CtaButton
              {...link}
              href={link.target}
              variant={link.variant}
              icon={link.icon}
              iconAlignment={link.iconAlignment}
              headerText={link.headerText}
            >
              {link.text}
            </CtaButton>
          </ButtonWrapper>
        ))}
    </StyledSimplifiedHero>
  );
};

SimplifiedHero.displayName = 'SimplifiedHero';

SimplifiedHero.propTypes = {
  heading: PropTypes.string.isRequired,
  headingVariants: PropTypes.array,
  description: PropTypes.node.isRequired,
  backgroundColor: PropTypes.string,
  link: PropTypes.object,
  level: PropTypes.string,
  eyebrow: PropTypes.string,
  typography: PropTypes.string,
  fontColor: PropTypes.string,
  mobilePadding: PropTypes.number,
  desktopPadding: PropTypes.number,
  textAlignment: PropTypes.oneOf(['left', 'center', 'right']),
};
